// Imports
import React, { useContext, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components/macro';
import AppContext from '../../context';


// Styled component
const Container = styled.ins`
	margin-top: 1.875rem;
`;


// Extend interface
declare global {
	interface Window {
		adsbygoogle?: unknown[] & { loaded?: boolean };
	}
}


// Function component
const GoogleAds: React.FC = () => {
	// Use ref
	const adRef = useRef<HTMLModElement>(null);
	const queuedEntryRef = useRef<object | null>(null);
	
	
	// Use React Router functionality
	const location = useLocation();
	
	
	// Use context
	const appContainerRef = useContext(AppContext)?.appContainerRef;
	
	
	// Trigger ad if necessary
	useEffect(() => {
		// Initialize
		const ad = adRef.current;
		
		
		// Trigger ad, unless one is already filled or requested for this slot, or if the slot has no width for ads
		if (ad && !ad.dataset.adsbygoogleStatus && queuedEntryRef.current === null && ad.offsetWidth > 0) {
			const entry = {};
			
			queuedEntryRef.current = entry;
			
			(window.adsbygoogle = window.adsbygoogle || []).push(entry);
		}
		
		
		// Capture current ref
		const node = appContainerRef?.current;
		
		
		// Watch for and undo injected style that breaks page layout
		const observer = new MutationObserver(() => {
			if (node?.style.minHeight) {
				node.style.minHeight = '';
			}
		});
		
		if (node) {
			observer.observe(node, { attributeFilter: ['style'] });
		}
		
		
		// Clean-up
		return () => {
			// Stop watching for injected style
			observer.disconnect();
			
			
			// Undo injected style that breaks page layout
			if (node) {
				node.style.minHeight = '';
			}
			
			
			// Withdraw this slot's ad request if the library hasn't processed it yet
			const queue = window.adsbygoogle;
			const entry = queuedEntryRef.current;
			
			if (entry && queue && !queue.loaded && !ad?.dataset.adsbygoogleStatus) {
				const index = queue.indexOf(entry);
				
				if (index !== -1) {
					queue.splice(index, 1);
				}
				
				queuedEntryRef.current = null;
			}
		};
	}, [location.pathname, appContainerRef]);
	
	
	// Return JSX
	return (
		<React.Fragment>
			<Helmet>
				<script async src='https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js' />
			</Helmet>
			
			<Container
				ref={adRef}
				className='adsbygoogle'
				style={{ display: 'block', overflow: 'hidden' }}
				data-ad-client='ca-pub-5774021149745769'
				data-ad-slot='1591685952'
				data-ad-format='auto'
			/>
		</React.Fragment>
	);
};

export default GoogleAds;
